import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Lock, Users, ChevronRight, Key, Database, Server, Globe } from 'lucide-react';

// Open Source Identity Security Platforms
const openSourcePlatforms = [
  { name: 'Keycloak', desc: 'Identity & Access Management' },
  { name: 'Authentik', desc: 'Identity Provider' },
  { name: 'Gluu', desc: 'IAM Platform' },
  { name: 'FreeIPA', desc: 'Identity Management' },
  { name: 'OpenLDAP', desc: 'Directory Services' },
  { name: 'Authelia', desc: 'SSO & 2FA' },
  { name: 'Dex', desc: 'OIDC Identity' },
  { name: 'ORY', desc: 'Cloud Native IAM' },
  { name: 'WSO2', desc: 'Identity Server' },
  { name: 'Apache Syncope', desc: 'Identity Management' },
  { name: 'CAS', desc: 'Enterprise SSO' },
  { name: 'Shibboleth', desc: 'Federated Identity' },
];

// Network nodes animation component
const NetworkAnimation = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient mesh overlay */}
      <div className="absolute inset-0 bg-mesh-pattern opacity-70" />

      {/* Animated grid lines */}
      <div className="absolute inset-0 network-lines" />

      {/* Floating nodes */}
      <div className="absolute top-1/4 left-1/4 node-dot" style={{ animationDelay: '0s' }} />
      <div className="absolute top-1/3 right-1/3 node-dot" style={{ animationDelay: '0.5s' }} />
      <div className="absolute bottom-1/4 left-1/3 node-dot" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 right-1/4 node-dot" style={{ animationDelay: '1.5s' }} />
      <div className="absolute bottom-1/3 right-1/2 node-dot" style={{ animationDelay: '0.7s' }} />

      {/* Decorative circles */}
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-brand/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
    </div>
  );
};

// Stats component
const StatItem = ({ value, label }) => (
  <div className="text-center px-6 py-4">
    <div className="text-3xl md:text-4xl font-bold text-dark-text mb-1">{value}</div>
    <div className="text-sm text-dark-muted">{label}</div>
  </div>
);

// Open Source Platform Badge
const PlatformBadge = ({ platform }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-dark-elevated/80 border border-dark-border text-xs font-medium text-dark-text hover:border-accent/50 transition-colors">
    <Key className="w-3 h-3 text-accent" />
    {platform.name}
  </div>
);

export const HeroSection = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef.current?.querySelectorAll('.reveal-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen bg-dark-section overflow-hidden"
    >
      <NetworkAnimation />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="reveal-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-dark-elevated border border-dark-border mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              <span className="text-sm text-dark-muted">Open Source Security Experts</span>
            </div>

            {/* Headline */}
            <h1 className="reveal-up font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-dark-text leading-tighter mb-6">
              Open Source Identity Security & <span className="text-gradient-brand">IAM Solutions</span> Worldwide
            </h1>

            {/* Subheadline */}
            <p className="reveal-up text-lg text-dark-muted max-w-xl mx-auto lg:mx-0 mb-8" style={{ animationDelay: '0.1s' }}>
              We provide enterprise-grade open source IAM, SSO, and identity security solutions
              to businesses worldwide. Leveraging platforms like Keycloak, Authentik, and more —
              we engineer identity-first protection without vendor lock-in.
            </p>

            {/* CTAs */}
            <div className="reveal-up flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" style={{ animationDelay: '0.2s' }}>
              <Button
                variant="brand"
                size="xl"
                onClick={() => window.open('https://calendar.app.google/TzJ8GRKhzddT7Kqp9', '_blank')}
                className="group"
              >
                Book Security Consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="dark-outline"
                size="xl"
                onClick={() => window.location.href = 'mailto:sales@hakxcore.com'}
              >
                Contact Sales
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="reveal-up mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-6" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-2 text-dark-muted">
                <Shield className="w-5 h-5 text-accent" />
                <span className="text-sm">Open Source First</span>
              </div>
              <div className="flex items-center gap-2 text-dark-muted">
                <Lock className="w-5 h-5 text-accent" />
                <span className="text-sm">No Vendor Lock-in</span>
              </div>
              <div className="flex items-center gap-2 text-dark-muted">
                <Users className="w-5 h-5 text-accent" />
                <span className="text-sm">50+ Enterprise Clients</span>
              </div>
            </div>
          </div>

          {/* Right Visual - Open Source Identity Illustration */}
          <div className="reveal-up relative hidden lg:block" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              {/* Main visual card - Open Source Identity Hub */}
              <div className="relative z-10 rounded-2xl overflow-hidden border border-dark-border bg-dark-bg/95 p-8 shadow-2xl">
                {/* Central Icon */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-accent/20 to-brand/20 flex items-center justify-center border border-accent/30">
                      <Key className="w-12 h-12 text-accent" />
                    </div>
                    {/* Orbiting icons */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-lg bg-dark-bg border border-dark-border flex items-center justify-center animate-float">
                      <Database className="w-4 h-4 text-brand" />
                    </div>
                    <div className="absolute -bottom-2 -left-2 w-8 h-8 rounded-lg bg-dark-bg border border-dark-border flex items-center justify-center animate-float" style={{ animationDelay: '1s' }}>
                      <Server className="w-4 h-4 text-accent" />
                    </div>
                    <div className="absolute top-1/2 -right-4 w-8 h-8 rounded-lg bg-dark-bg border border-dark-border flex items-center justify-center animate-float" style={{ animationDelay: '2s' }}>
                      <Globe className="w-4 h-4 text-success" />
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-6">
                  <h2 className="text-lg font-semibold text-dark-text mb-1">Open Source Identity Hub</h2>
                  <p className="text-sm text-dark-muted">Enterprise-grade IAM solutions</p>
                </div>

                {/* Platform badges grid */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {openSourcePlatforms.slice(0, 8).map((platform, index) => (
                    <PlatformBadge key={index} platform={platform} />
                  ))}
                </div>

                {/* Stats */}
                <div className="mt-6 pt-6 border-t border-dark-border grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-dark-text">12+</div>
                    <div className="text-xs text-dark-muted">Platforms</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-dark-text">1M+</div>
                    <div className="text-xs text-dark-muted">Users Protected</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-dark-text">99.9%</div>
                    <div className="text-xs text-dark-muted">Uptime</div>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -top-12 -right-12 z-20 glass-dark bg-dark-bg/95 rounded-xl p-4 shadow-glow-brand animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand/30 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-brand" />
                  </div>
                  <div>
                    <div className="text-xs text-dark-muted font-bold">Zero Trust</div>
                    <div className="text-sm font-semibold text-dark-text">Active Protection</div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-10 -left-12 z-20 glass-dark bg-dark-bg/95 rounded-xl p-4 shadow-glow-brand animate-float" style={{ animationDelay: '2s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-success/30 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <div className="text-xs text-dark-muted font-bold">Open Source</div>
                    <div className="text-sm font-semibold text-dark-text">No Vendor Lock-in</div>
                  </div>
                </div>
              </div>

              {/* Glow effect */}
              <div className="absolute inset-0 -z-10 blur-3xl opacity-30">
                <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-brand rounded-full" />
                <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 bg-accent rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Open Source Platforms Section */}
        <div className="reveal-up mt-16 pt-10 border-t border-dark-border" style={{ animationDelay: '0.35s' }}>
          <p className="text-center text-sm text-dark-muted mb-6">
            Experts in Open Source Identity Security Platforms
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {openSourcePlatforms.map((platform, index) => (
              <div
                key={index}
                className="px-4 py-2 rounded-full bg-dark-elevated border border-dark-border text-sm font-medium text-dark-text hover:border-accent/50 hover:text-accent transition-all duration-200 cursor-default"
              >
                {platform.name}
              </div>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="reveal-up mt-12 pt-10 border-t border-dark-border" style={{ animationDelay: '0.4s' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatItem value="50+" label="Enterprise Clients" />
            <StatItem value="99.9%" label="System Uptime" />
            <StatItem value="1M+" label="Identities Secured" />
            <StatItem value="24/7" label="Support Available" />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronRight className="w-6 h-6 text-dark-muted rotate-90" />
      </div>
    </section>
  );
};

export default HeroSection;
